import React, { useEffect, useState } from 'react';
import { List, ListItem, useMediaQuery } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useDispatch, useSelector } from 'react-redux';
import api from '../api/api';
import { setUserDetails } from '../redux/userSlice';
import { sidebarListItems } from '../utils/sidebarList';
import { isAdminLevelUser } from '../utils/roles';

const CustomSideNav = ({ isCollapsed, setIsCollapsed }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector(state => state.user.userDetails);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [tokenChecked, setTokenChecked] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const accessToken = queryParams.get('allow');
    if (accessToken) setToken(accessToken);
    setTokenChecked(true);
  }, [location.search]);

  useEffect(() => {
    const fetchUserRole = async () => {
      
      try {
        setLoading(true);
        const response = await api.get('/auth/me');

        dispatch(setUserDetails(response));
      } catch (error) {
        console.error('Failed to fetch user role:', error);
      } finally {
        setLoading(false);
      }
    };

    if (!user?.user_roles && !token) fetchUserRole();
  }, [user, token, dispatch]);

  const miniSidenav = isCollapsed;

  useEffect(() => {
    setIsCollapsed(isMobile);
  }, [isMobile, setIsCollapsed]);

  const handleCollapse = () => setIsCollapsed(prev => !prev);
// Map permissions to sidebar items
const userPermissions = user?.user_info?.permissions || [];




const allSidebarItems = sidebarListItems(token);

// Items everyone can see regardless of permissions. '17' = Subcontractors.
// const alwaysVisibleIds = ['17'];

const isAdmin = isAdminLevelUser(user);
// Items only Admins should ever see, regardless of stored permissions.
// '18' = OTP Logs (login & validation OTPs are sensitive).
// '20' = Activity Trail (full user activity audit — admin eyes only).
const adminOnlyIds = ['18', '20'];

const filteredNavItems = allSidebarItems.filter(item => {
  const id = item.id.toString();
  // if (alwaysVisibleIds.includes(id)) return true;
  if (adminOnlyIds.includes(id)) return isAdmin;
  return userPermissions.includes(id);
});

  return (
    <>
      <style>
        {`
          .sidenav-item {
            transition: all 0.2s ease;
            position: relative;
          }
          .sidenav-item:hover:not(.active) {
            background-color: #F3F6FB;
          }
          .sidenav-item:hover:not(.active) .sidenavlabel {
            color: #145388;
          }
          .sidenav-item:hover:not(.active) .sidenav-icon svg,
          .sidenav-item:hover:not(.active) .sidenav-icon img {
            filter: none;
          }
          .sidenav-item.active {
            background: linear-gradient(90deg, #145388 0%, #1E6BA8 100%);
            box-shadow: 0 4px 12px -3px rgba(20, 83, 136, 0.45);
          }
          .sidenav-item.active::before {
            content: '';
            position: absolute;
            left: -8px;
            top: 10%;
            height: 80%;
            width: 3px;
            background: #145388;
            border-radius: 0 4px 4px 0;
          }
          .sidenav-item.active .sidenavlabel {
            color: white;
          }
          .sidenav-item.active .sidenav-icon svg,
          .sidenav-item.active .sidenav-icon img {
            filter: brightness(0) invert(1);
          }
          .sidenav-collapse-btn {
            width: 26px;
            height: 26px;
            border-radius: 50%;
            background: #F3F6FB;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.15s ease;
          }
          .sidenav-collapse-btn:hover {
            background: #E5ECF5;
          }
          .sidenav-scroll::-webkit-scrollbar {
            width: 4px;
          }
          .sidenav-scroll::-webkit-scrollbar-thumb {
            background: #D1D5DB;
            border-radius: 2px;
          }
          .sidenav-scroll::-webkit-scrollbar-thumb:hover {
            background: #9CA3AF;
          }
        `}
      </style>

      <div
        className={`sidenav-scroll min-h-[70vh] max-h-[calc(100vh-100px)] overflow-y-auto bg-white rounded-b-xl shadow-[0px_14px_42px_0px_rgba(0,_0,_0,_0.06)] transition-all duration-300 ${miniSidenav ? 'w-[64px]' : 'w-[220px] min-w-[220px]'}`}
      >
        {/* Header with collapse button */}
        <div className={`flex items-center px-3 pt-3 pb-2 ${miniSidenav ? 'justify-center' : 'justify-between'}`}>
          {!miniSidenav && (
            <div className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
              Menu
            </div>
          )}
          <div onClick={handleCollapse} className="sidenav-collapse-btn">
            {miniSidenav ? (
              <ArrowForwardIosIcon style={{ fontSize: 12, color: '#6B7280' }} />
            ) : (
              <ArrowBackIosIcon style={{ fontSize: 12, color: '#6B7280', marginLeft: 3 }} />
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="mx-3 h-px bg-gray-100 mb-2" />

        {/* Menu items */}
        <div className="flex flex-col w-full px-2 pb-4">
          <List disablePadding className="w-full flex flex-col gap-1">
            {filteredNavItems.map((item, index) => {
              const isActive =
                location.pathname === new URL(item.path, window.location.origin).pathname;
              return (
                <ListItem
                  key={index}
                  className={`sidenav-item cursor-pointer rounded-lg ${
                    isActive ? 'active' : ''
                  }`}
                  button
                  style={{
                    padding: miniSidenav ? '10px 0' : '10px 12px',
                    justifyContent: miniSidenav ? 'center' : 'flex-start',
                  }}
                  onClick={() => navigate(item.path)}
                  title={miniSidenav ? item.label : ''}
                >
                  <div
                    className="sidenav-icon flex items-center justify-center transition-colors"
                    style={{ width: 22, height: 22, minWidth: 22 }}
                  >
                    {item.icon}
                  </div>
                  {!miniSidenav && (
                    <div
                      className={`sidenavlabel ml-3 text-[13px] font-medium whitespace-nowrap transition-colors ${
                        isActive ? 'text-white' : 'text-gray-700'
                      }`}
                    >
                      {item.label}
                    </div>
                  )}
                </ListItem>
              );
            })}
          </List>
        </div>
      </div>
    </>
  );
};

export default CustomSideNav;
